import { useEffect, useState, useCallback } from "react";
import { Sparkles, FileText, Check, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const STATUS_STYLE = {
  new: "bg-amber/15 text-amber",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-softgrey text-ink/40",
  drafted: "bg-burgundy/10 text-burgundy",
};

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [drafting, setDrafting] = useState("");

  const load = useCallback(() => {
    apiFetch("/admin/opportunities").then(setItems).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await apiFetch("/admin/opportunities/generate", { method: "POST" });
      toast.success(`${res.created} new opportunities generated`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (id, status) => {
    await apiFetch(`/admin/opportunities/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    load();
  };

  const draft = async (id) => {
    setDrafting(id);
    try {
      await apiFetch(`/admin/opportunities/${id}/draft`, { method: "POST" });
      toast.success("Draft created — review it in Content before publishing");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDrafting("");
    }
  };

  return (
    <div data-testid="admin-opportunities">
      <Toaster position="top-center" richColors />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Daily content engine</p>
          <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Today&apos;s content opportunities</h1>
          <p className="mt-3 max-w-xl text-sm text-ink/60">
            Three per vertical, ranked by relevance and intent. Nothing publishes
            without your approval.
          </p>
        </div>
        <button onClick={generate} disabled={generating} className="btn-primary !px-5 !py-2.5" data-testid="opportunities-generate">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} /> {generating ? "Researching…" : "Generate Today's Opportunities"}
        </button>
      </div>

      {["dental", "consulting", "publishing"].map((v) => {
        const list = items.filter((o) => o.vertical === v);
        return (
          <section key={v} className="mt-10" data-testid={`opportunities-${v}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">{v} — {list.length}</p>
            {list.length === 0 ? (
              <p className="mt-3 text-sm text-ink/40">No opportunities yet. Generate today's set.</p>
            ) : (
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                {list.map((o) => (
                  <article key={o.id} className="flex flex-col border border-ink/10 bg-warmwhite p-6" data-testid={`opportunity-${o.id.slice(0, 8)}`}>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif text-base leading-snug text-ink">{o.headline}</h3>
                      <span className={`shrink-0 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLE[o.status]}`}>{o.status}</span>
                    </div>
                    <dl className="mt-4 flex-1 space-y-2 text-xs text-ink/65">
                      <div><dt className="inline font-semibold text-ink/80">Audience: </dt><dd className="inline">{o.audience}</dd></div>
                      <div><dt className="inline font-semibold text-ink/80">Intent: </dt><dd className="inline">{o.search_intent}</dd></div>
                      <div><dt className="inline font-semibold text-ink/80">Keyword: </dt><dd className="inline">{o.keyword}</dd></div>
                      <div><dt className="inline font-semibold text-ink/80">Why now: </dt><dd className="inline">{o.why_now}</dd></div>
                      <div><dt className="inline font-semibold text-ink/80">CTA: </dt><dd className="inline">{o.cta}</dd></div>
                    </dl>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button onClick={() => draft(o.id)} disabled={drafting === o.id} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/70 hover:border-amber" data-testid={`opportunity-draft-${o.id.slice(0, 8)}`}>
                        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> {drafting === o.id ? "Drafting…" : "Generate Draft"}
                      </button>
                      <button onClick={() => setStatus(o.id, "approved")} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/70 hover:border-emerald-400" data-testid={`opportunity-approve-${o.id.slice(0, 8)}`}>
                        <Check className="h-3.5 w-3.5" strokeWidth={1.5} /> Approve
                      </button>
                      <button onClick={() => setStatus(o.id, "rejected")} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/40 hover:border-red-400" data-testid={`opportunity-reject-${o.id.slice(0, 8)}`}>
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} /> Reject
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
